import React, { useState, useEffect } from 'react';
import { X, Download, AlertCircle, CheckCircle, Clock, Loader2, FileDown, Image, FileText, XCircle, Eye, ExternalLink, Hash, RefreshCw, Trash2, PauseCircle } from 'lucide-react';

const ExportProgress = ({ jobs, onDownload, onCancel, onClose, isExpanded, onCollapse, onSync, onSyncAll, onClearQueue }) => {
  const [hiddenJobs, setHiddenJobs] = useState(new Set());
  const [syncing, setSyncing] = useState(false);
  const [clearing, setClearing] = useState(false);
  // Ticks every second so pending-item countdown timers update live.
  const [nowTick, setNowTick] = useState(Date.now());

  // Filter out hidden jobs
  const visibleJobs = jobs.filter(job => !hiddenJobs.has(job.id));

  useEffect(() => {
    // Clean up hidden jobs that no longer exist
    setHiddenJobs(prev => {
      const jobIds = new Set(jobs.map(job => job.id));
      return new Set([...prev].filter(id => jobIds.has(id)));
    });
  }, [jobs]);

  // Drive the per-second countdown only while there is at least one pending/processing job.
  useEffect(() => {
    const hasPending = visibleJobs.some(j => ['starting', 'processing', 'queued', 'pending'].includes(String(j.status).toLowerCase()));
    if (!hasPending) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [visibleJobs]);

  const handleSyncAll = async () => {
    if (!onSyncAll || syncing) return;
    setSyncing(true);
    try { await onSyncAll(); } finally { setSyncing(false); }
  };

  const handleClearQueue = async () => {
    if (!onClearQueue || clearing) return;
    if (!window.confirm('Clear all your progress records? This removes them for your account only.')) return;
    setClearing(true);
    try { await onClearQueue(); } finally { setClearing(false); }
  };

  // Only show if expanded and there are visible jobs
  if (!isExpanded || visibleJobs.length === 0) {
    return null;
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'cancelled':
        return <X className="w-5 h-5 text-gray-500" />;
      case 'suspended':
        return <PauseCircle className="w-5 h-5 text-amber-500" />;
      case 'starting':
      case 'processing':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 border-green-200';
      case 'failed':
        return 'bg-red-50 border-red-200';
      case 'cancelled':
        return 'bg-gray-50 border-gray-200';
      case 'suspended':
        return 'bg-amber-50 border-amber-200';
      case 'starting':
      case 'processing':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getFormatIcon = (format) => {
    switch (format?.toLowerCase()) {
      case 'pdf':
        return <FileText className="w-4 h-4 text-red-500" />;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'tif':
      case 'tiff':
        return <Image className="w-4 h-4 text-blue-500" />;
      default:
        return <FileDown className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusMessage = (job) => {
    const { status, format, message, progress = 0, jobType } = job;
    
    if (message) {
      return message;
    }

    // AI image generation jobs read as "AI Image" (matching the old snackbar),
    // not as a file export.
    const isAi = jobType === 'ai_image';
    const label = isAi ? 'AI Image' : (format?.toUpperCase() || 'Export');
    
    switch (status) {
      case 'starting':
        return isAi ? `🚀 Starting AI Image generation...` : `🚀 Starting ${label}...`;
      case 'processing':
        return isAi ? `🎨 Generating AI Image... ${Math.round(progress)}%` : `📄 Exporting ${label}... ${Math.round(progress)}%`;
      case 'completed':
        return isAi ? `✨ AI Image ready!` : `✨ ${label} ready for download!`;
      case 'failed':
        return `❌ ${label} failed`;
      case 'cancelled':
        return `⏹️ ${label} cancelled`;
      case 'suspended':
        return `⏸️ ${label} suspended (pending over 1h) — click Sync to retry`;
      default:
        return '...';
    }
  };

  const getProgressBarColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      case 'cancelled':
        return 'bg-gray-400';
      case 'starting':
      case 'processing':
        return 'bg-blue-500';
      default:
        return 'bg-gray-400';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const calculateDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    
    if (diffHours > 0) {
      const remainingMinutes = diffMinutes % 60;
      return `${diffHours}h ${remainingMinutes}m`;
    } else if (diffMinutes > 0) {
      const remainingSeconds = diffSeconds % 60;
      return `${diffMinutes}m ${remainingSeconds}s`;
    } else {
      return `${diffSeconds}s`;
    }
  };

  const handleDownload = (job) => {
    if (job.downloadUrl && job.filename) {
      const a = document.createElement('a');
      a.href = job.downloadUrl;
      a.download = job.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(job.downloadUrl);
      
      if (onDownload) {
        onDownload(job);
      }
    }
  };

  const handlePreview = (job) => {
    if (job.downloadUrl || job.image_url || job.final_url) {
      const url = job.downloadUrl || job.image_url || job.final_url;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCancel = (jobId) => {
    if (onCancel) {
      onCancel(jobId);
    }
  };

  const handleClose = (jobId) => {
    // Hide the job instead of removing it
    setHiddenJobs(prev => new Set([...prev, jobId]));
  };

  const handleCollapse = () => {
    if (onCollapse) {
      onCollapse();
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden backdrop-blur-sm overflow-x-hidden">
        {/* Header - Enhanced */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <h3 className="text-sm font-semibold text-gray-900">Progress Tracker ({visibleJobs.length})</h3>
          </div>
          <div className="flex items-center gap-1">
            {onSyncAll && (
              <button
                onClick={handleSyncAll}
                disabled={syncing}
                className="text-gray-500 hover:text-blue-600 transition-colors p-1 hover:bg-white rounded disabled:opacity-50"
                title="Sync all pending jobs from the workflow queue"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={handleCollapse}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-white rounded"
              title="Minimize to footer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Jobs List - Latest on top (already reversed) - Enhanced appearance */}
        <div className="max-h-96 overflow-y-auto overflow-x-hidden" style={{ scrollbarWidth: 'thin', scrollbarColor: '#888 #f1f1f1' }}>
          {visibleJobs.slice().reverse().map((job, index) => (
            <div
              key={job.id}
              className={`p-4 border-b border-gray-100 last:border-b-0 transition-all duration-300 hover:shadow-lg ${getStatusColor(job.status)} ${
                index === 0 ? 'bg-gradient-to-r from-blue-50/50 to-transparent border-l-4 border-l-blue-500' : ''
              }`}
            >
              {/* Two-column layout: Preview on left, Info on right */}
              <div className="flex gap-4 mb-4 overflow-hidden">
                {/* Left side: Full-height preview */}
                <div className="flex-shrink-0">
                  {(job.thumbnail_url || job.preview_url || job.image_url || job.video_url || job.downloadUrl || job.final_url) && (
                    <div className="w-32 h-40 bg-gray-100 rounded-lg border overflow-hidden cursor-pointer group relative"
                         onClick={() => handlePreview(job)}
                         title="Click to open preview in new tab">
                      <img
                        src={job.thumbnail_url || job.preview_url || job.image_url || job.video_url || job.downloadUrl || job.final_url}
                        alt="Export preview"
                        className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400 hidden">
                        {getFormatIcon(job.format)}
                      </div>
                      {/* Hover overlay with instruction */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded px-2 py-1 text-xs text-gray-700 font-medium">
                          Click to preview
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right side: All information */}
                <div className="flex-1 min-w-0 overflow-hidden">
                  {/* Job Header */}
                  <div className="flex items-start justify-between mb-2 gap-2">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {getStatusIcon(job.status)}
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {job.jobType === 'ai_image'
                            ? 'AI Image'
                            : (job.format?.toUpperCase() || 'Export')}
                        </span>
                        {/* Queue ID Display */}
                        {job.id && (
                          <div className="flex items-center gap-1 text-gray-400 flex-shrink-0">
                            <Hash className="w-3 h-3" />
                            <span className="font-mono text-xs">{job.id}</span>
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        <span className="capitalize">{job.status}</span>
                        {job.colorMode && (
                          <span className="ml-2 px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                            {job.colorMode}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 ml-2">
                      {/* Per-job sync for in-flight jobs */}
                      {['starting', 'processing', 'queued', 'pending'].includes(String(job.status).toLowerCase()) && onSync && (
                        <button
                          onClick={() => onSync(job.id)}
                          className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                          title="Sync this job now"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                      {/* Cancel button for active jobs */}
                      {(job.status === 'starting' || job.status === 'processing') && onCancel && (
                        <button
                          onClick={() => handleCancel(job.id)}
                          className="text-red-400 hover:text-red-600 transition-colors p-1"
                          title="Cancel export"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}

                      <button
                        onClick={() => handleClose(job.id)}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                        title="Hide notification"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Progress Bar - Show for active statuses */}
                  {(job.status === 'starting' || job.status === 'processing') && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                        <span className="font-medium">Progress</span>
                        <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                          {Math.round(job.progress || 0)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                        <div
                          className={`h-3 rounded-full transition-all duration-500 ease-out ${getProgressBarColor(job.status)}`}
                          style={{
                            width: `${Math.max(job.progress || 0, 5)}%`,
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
                          }}
                        >
                          {/* Progress bar shine effect */}
                          <div className="h-full w-full rounded-full bg-gradient-to-r from-transparent via-white to-transparent opacity-30"></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Status Message */}
                  <div className="mb-3 overflow-hidden">
                    <div className="text-sm text-gray-700 font-medium mb-1 truncate">
                      {getStatusMessage(job)}
                    </div>
                    
                    {/* Details line */}
                    <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded flex items-center gap-2 overflow-hidden">
                      <span className="flex-shrink-0">
                        {job.jobType === 'ai_image' ? <Image className="w-4 h-4 text-indigo-500" /> : getFormatIcon(job.format)}
                      </span>
                      <span className="truncate">
                        {job.jobType === 'ai_image' ? 'AI Image' : (job.format?.toUpperCase() || 'FILE')}
                        {job.progressText && job.jobType === 'ai_image' ? ` • ${job.progressText}` : ''}
                        {job.colorMode && job.jobType !== 'ai_image' && ` • ${job.colorMode}`}
                        {job.currentPageIndex !== null && job.currentPageIndex !== undefined && 
                          ` • Page ${job.currentPageIndex + 1}`}
                      </span>
                    </div>
                  </div>

                  {/* Duration - Show time taken to generate (fixed calculation) */}
                  {job.created_at && (
                    <div className="text-xs text-gray-600 mb-2 bg-white/60 px-2 py-1.5 rounded border border-gray-200 overflow-hidden">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                        <span className="font-medium truncate">
                          {job.status === 'completed' 
                            ? (() => {
                                // Use completed_at if available, otherwise calculate from updated_at or current time
                                const endTime = job.completed_at || job.updated_at || job.finished_at || new Date().toISOString();
                                const duration = calculateDuration(job.created_at, endTime);
                                return `⏱️ Generated in ${duration}`;
                              })()
                            : job.status === 'processing' || job.status === 'starting'
                            ? (() => {
                                const elapsed = calculateDuration(job.created_at, new Date().toISOString());
                                return `🔄 Started ${formatTime(job.created_at)} • Elapsed: ${elapsed}`;
                              })()
                            : `📅 Started ${formatTime(job.created_at)}`
                          }
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Task 3: Countdown to next poll for pending/processing jobs */}
                  {['starting', 'processing', 'queued', 'pending'].includes(String(job.status).toLowerCase()) && job.nextPollAt && (
                    <div className="text-xs text-blue-600 mb-2 flex items-center gap-1.5">
                      <RefreshCw className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>
                        {(() => {
                          const secs = Math.max(0, Math.ceil((job.nextPollAt - nowTick) / 1000));
                          return secs > 0 ? `Next sync in ${secs}s` : 'Syncing…';
                        })()}
                      </span>
                    </div>
                  )}

                  {/* Task 1/4: Suspended jobs get a manual Sync-now button */}
                  {job.status === 'suspended' && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onSync && onSync(job.id)}
                        className="p-2 bg-amber-500 text-white rounded hover:bg-amber-600 transition-colors flex items-center gap-1 text-xs"
                        title="Sync this job from the workflow queue now"
                      >
                        <RefreshCw className="w-4 h-4" /> Sync now
                      </button>
                      <button
                        onClick={() => handleClose(job.id)}
                        className="p-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                        title="Hide notification"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Actions */}
                  {job.status === 'completed' && (job.downloadUrl || job.image_url || job.final_url) && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDownload(job)}
                        className="p-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                        title="Download file"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handlePreview(job)}
                        className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                        title="Open preview in new tab"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleClose(job.id)}
                        className="p-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                        title="Close notification"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {(job.status === 'failed' || job.status === 'cancelled') && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleClose(job.id)}
                        className="p-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        title="Remove notification"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 py-2 border-t border-gray-200 flex items-center justify-between gap-2">
          <div className="text-xs text-gray-500">
            {(() => {
              const activeJobs = jobs.filter(j => ['starting', 'processing'].includes(j.status)).length;
              const completedJobs = jobs.filter(j => j.status === 'completed').length;
              const failedJobs = jobs.filter(j => ['failed', 'cancelled'].includes(j.status)).length;
              const suspendedJobs = jobs.filter(j => j.status === 'suspended').length;

              return `${activeJobs} active • ${completedJobs} completed • ${failedJobs} failed${suspendedJobs ? ` • ${suspendedJobs} suspended` : ''}`;
            })()}
          </div>
          {onClearQueue && (
            <button
              onClick={handleClearQueue}
              disabled={clearing}
              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50 flex-shrink-0"
              title="Clear all your progress records"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {clearing ? 'Clearing…' : 'Clear Queue'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportProgress;
